import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import { WidgetEditorContext } from '@juvare/widget-ui/WidgetEditorContext';
import SquareIcon from '@mui/icons-material/Square';
import { Alert, Box, Typography } from '@mui/material';
import React from 'react';
import { HtmlOutput } from '../HtmlOutput';
import { buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { defaultWidgetLabelProvider, handleWidgetUpdate, makeNewConfigUnique } from '../WidgetForm/Utils';
import { WidgetHandler } from '../WidgetHandler';
import { TemplateOutput } from './Components';
import { type } from './types';
const WidgetFormContent = (props) => {
    var _a, _b, _c, _d, _e;
    const context = React.useContext(WidgetEditorContext);
    let type;
    let content;
    if (props.config.render) {
        type = 'Custom';
        content = React.createElement(Typography, { component: 'div' }, props.config.render(new WidgetHandler(props)));
    }
    else if (props.config.template) {
        type = 'Template';
        content = (React.createElement(Typography, { component: 'div', "data-test": 'template' },
            React.createElement(TemplateOutput, { template: props.config.template, props: props })));
    }
    else if (props.config.html) {
        type = 'HTML';
        content = (React.createElement(Typography, { component: 'div' },
            React.createElement(HtmlOutput, { html: props.config.html, customTags: (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.htmlOutputCustomTags, data: (_b = props.rootParams.globalWidgetConfig) === null || _b === void 0 ? void 0 : _b.htmlOutputData, customIcons: (_c = props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.htmlOutputIcons })));
    }
    else {
        type = 'Text';
        content = React.createElement(Typography, null, props.config.text);
    }
    if ((_d = props.config.style) === null || _d === void 0 ? void 0 : _d.alert) {
        return React.createElement(Alert, { severity: (_e = props.config.style) === null || _e === void 0 ? void 0 : _e.alert }, content);
    }
    else if (context.editMode) {
        return (React.createElement(Box, null,
            React.createElement(Typography, { variant: 'body2' },
                type,
                ":"),
            content));
    }
    else {
        return content;
    }
};
const WidgetFormContentEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Content',
        }, buildGeneralTab(props, {
            children: [
                {
                    type: 'form.radio',
                    key: 'contentType',
                    values: [
                        { value: 'text', label: 'Text' },
                        { value: 'html', label: 'HTML' },
                        { value: 'template', label: 'Template' },
                    ],
                },
                {
                    type: 'form.text',
                    key: 'text',
                    label: 'Text',
                    multiline: true,
                    rows: 6,
                    hidden: {
                        when: '^contentType',
                        dataNotEq: 'text',
                    },
                },
                {
                    type: 'form.html',
                    key: 'html',
                    label: 'HTML',
                    rows: 10,
                    hidden: {
                        when: '^contentType',
                        dataNotEq: 'html',
                    },
                },
                {
                    type: 'form.template',
                    key: 'template',
                    label: 'Template',
                    hidden: {
                        when: '^contentType',
                        dataNotEq: 'template',
                    },
                    props: props,
                },
            ],
            onLoad: (data) => {
                if (data.template != null) {
                    data.contentType = 'template';
                }
                else if (data.html != null) {
                    data.contentType = 'html';
                }
                else {
                    data.contentType = 'text';
                }
                return data;
            },
            onSubmit: (data) => {
                if (data.contentType === 'template') {
                    delete data.html;
                    delete data.text;
                }
                else if (data.contentType === 'html') {
                    delete data.template;
                    delete data.text;
                }
                else {
                    delete data.template;
                    delete data.html;
                }
                delete data.contentType;
                return data;
            },
        }), buildHiddenTab(props), buildStyleTab(props, {
            children: [
                {
                    type: 'form.select',
                    key: 'style.alert',
                    label: 'Alert',
                    values: [
                        { value: 'info', label: 'Info' },
                        { value: 'error', label: 'Error' },
                        { value: 'warning', label: 'Warning' },
                        { value: 'success', label: 'Success' },
                    ],
                    style: {
                        width: 300,
                    },
                },
            ],
        })) }),
        React.createElement(WidgetFormContent, Object.assign({}, props))));
};
export const widgetFormContent = {
    type,
    update: (props) => {
        if (props.config.template || props.config.render) {
            // need to always rerender because template or render depends on values from other components
            props.update();
        }
        return handleWidgetUpdate(props);
    },
    component: WidgetFormContent,
    icon: React.createElement(SquareIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormContentEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'content', label: 'Content' }),
};
